<script setup lang="ts">
import {computed, ref, watch, nextTick} from 'vue';
import {getRandomId, roundTo} from '@/ts/utils/pure-functions';
import type {QuestionNumeric} from '@/ts/types/dto/interventions-dto';
import {
  type QuestionAnswerNumericBody,
  QuestionnaireType,
} from '@/ts/types/dto/session-request-dto';
import BadgePill from '@/vue/atoms/badge-pill.vue';
import FormRequired from '@/vue/atoms/form-required.vue';
import {useUserStore} from '@/ts/store/user-store';
import {
  useUnitConversion,
  WEIGHT_DECIMALS,
  WEIGHT_STEP,
} from '@/ts/composables/pure/use-unit-conversion';
import {useSessionWeight} from '@/ts/composables/stateful/use-session-weight';

const props = defineProps<{question: QuestionNumeric}>();

const model = defineModel<QuestionAnswerNumericBody | null>();
const {userData} = useUserStore();

const {gramsToUserUnit, userUnitToGrams, getWeightUnit} = useUnitConversion({
  countryCode: userData?.countryCode,
});

const displayValue = ref<string>('');
const touched = ref<boolean>(false);
let internalUpdate = false;
const unitLabel = computed(() => getWeightUnit());

const inputLabelledby = `question-${getRandomId()}`;

/**
 * Raw BE limits in grams.
 */
const rawMinGrams = computed(() => props.question.numeric.minValue ?? 0);
const rawMaxGrams = computed(() => {
  const max = props.question.numeric.maxValue ?? 400000;
  return Math.max(max, rawMinGrams.value);
});

/**
 * Calculate displayable min/max that will actually validate correctly.
 * We need to ensure that when user enters these values and they're converted
 * back to grams, the result is within the valid range.
 */
function snapWeightMin(minGrams: number): number {
  let userUnit = gramsToUserUnit(minGrams);
  userUnit = roundTo(userUnit, WEIGHT_DECIMALS);

  // Increment until the rounded gram value is >= min
   
  while (true) {
    const backToGrams = Math.round(userUnitToGrams(userUnit));
    if (backToGrams >= minGrams) {
      return userUnit;
    }
    userUnit = roundTo(userUnit + WEIGHT_STEP, WEIGHT_DECIMALS);
  }
}

function snapWeightMax(maxGrams: number): number {
  let userUnit = gramsToUserUnit(maxGrams);
  userUnit = roundTo(userUnit, WEIGHT_DECIMALS);

  // Decrement until the rounded gram value is <= max
   
  while (true) {
    const backToGrams = Math.round(userUnitToGrams(userUnit));
    if (backToGrams <= maxGrams) {
      return userUnit;
    }
    userUnit = roundTo(userUnit - WEIGHT_STEP, WEIGHT_DECIMALS);
  }
}

const min = computed(() => snapWeightMin(rawMinGrams.value));
const max = computed(() => snapWeightMax(rawMaxGrams.value));

const {getPrefill, recordWeight} = useSessionWeight();

function clampGramsToRange(grams: number): number {
  return Math.min(Math.max(grams, rawMinGrams.value), rawMaxGrams.value);
}

function setModel(grams: number | null, isValidInput = false): void {
  internalUpdate = true;
  if (grams === null) {
    model.value = null;
  } else {
    const clamped = clampGramsToRange(Math.round(grams));
    model.value = {numeric: clamped, type: QuestionnaireType.NUMERIC};

    // Only record if the input was valid (not clamped due to out-of-range)
    if (isValidInput) {
      recordWeight(props.question.contentId, clamped);
    }
  }
  void nextTick(() => {
    internalUpdate = false;
  });
}

// Show up to WEIGHT_DECIMALS decimals but drop a trailing zero ("255" not "255.0"), and always with
// a dot — never a locale comma. A native <input type="number"> localises its display (a Spain region
// renders "255,0"), so the field is a plain text/decimal input rendering this string verbatim.
function formatWeightDisplay(userUnit: number): string {
  return String(roundTo(userUnit, WEIGHT_DECIMALS));
}

function calculateUserUnit(grams: number): number {
  if (grams === rawMinGrams.value) {
    return min.value;
  }
  if (grams === rawMaxGrams.value) {
    return max.value;
  }
  return roundTo(gramsToUserUnit(grams), WEIGHT_DECIMALS);
}

function handleModelWithValue(grams: number): void {
  const userUnit = calculateUserUnit(grams);
  displayValue.value = formatWeightDisplay(userUnit);
}

function handleModelNull(): void {
  const hasNoAnswer = props.question.numericSelected === null || props.question.numericSelected === void 0;
  const prefillWeight = hasNoAnswer ? getPrefill(props.question.contentId) : null;

  if (prefillWeight === null) {
    // No session weight or question already answered - leave empty
    displayValue.value = '';
    // Explicitly set model to null to ensure validation works
    if (model.value !== null) {
      setModel(null);
    }
    return;
  }

  // Pre-fill with the weight already entered earlier in this session
  const clamped = clampGramsToRange(Math.round(prefillWeight));
  const userUnit = calculateUserUnit(clamped);

  displayValue.value = formatWeightDisplay(userUnit);
  model.value = {numeric: clamped, type: QuestionnaireType.NUMERIC};
}

watch(
    () => model.value,
    (newValue) => {
      if (internalUpdate) {
        return;
      }

      if (newValue?.numeric !== null && newValue?.numeric !== void 0) {
        handleModelWithValue(newValue.numeric);
      } else {
        handleModelNull();
      }
    },
    {immediate: true},
);

function onInput(val: string | number | null): void {
  const s = String(val ?? '');
  displayValue.value = s?.replace(',', '.') ?? s;
}

const isValid = computed((): boolean => {
  const trimmed = displayValue.value.trim();
  if (trimmed === '') {
    return false;
  }
  const valNum = parseFloat(trimmed);
  return !Number.isNaN(valNum) && valNum >= min.value && valNum <= max.value;
});

// Validation rules for QForm - returns empty string to hide Quasar's error message
// The actual error message is shown by form-required component
const validationRules = [
  (): true | string => {
    return isValid.value ? true : '';
  },
];

function roundAndUpdate(valNum: number): void {
  const rounded = roundTo(valNum, WEIGHT_DECIMALS);
  displayValue.value = formatWeightDisplay(rounded);

  // Check if the value is within valid range
  const isWithinRange = rounded >= min.value && rounded <= max.value;

  const grams = userUnitToGrams(rounded);
  setModel(grams, isWithinRange);
}

function handleBlur(): void {
  touched.value = true;

  if (!displayValue.value) {
    setModel(null);
    return;
  }

  const valNum = parseFloat(displayValue.value);

  if (Number.isNaN(valNum)) {
    setModel(null);
    displayValue.value = '';
    return;
  }

  roundAndUpdate(valNum);
}
</script>

<template>
  <div class="question-weight-input">
    <badge-pill theme="blue" :label="$t('question')"/>
    <div :id="inputLabelledby" v-safe-html="question.name" class="question text-h6"/>
    <div class="input-container">
      <q-input
        :model-value="displayValue"
        class="input"
        type="text"
        inputmode="decimal"
        :aria-labelledby="inputLabelledby"
        :readonly="question.isAnswered"
        :disable="question.isAnswered"
        :rules="validationRules"
        lazy-rules
        hide-bottom-space
        @update:model-value="onInput"
        @blur="handleBlur"
      />
      <span class="unit-label">{{ unitLabel }}</span>
    </div>
    <form-required
      v-if="touched"
      id="weight-validation"
      :valid="isValid"
      :message="$t('validation_number_error_msg', {
        min: min.toFixed(WEIGHT_DECIMALS),
        max: max.toFixed(WEIGHT_DECIMALS)
      })"
    />
    <form-required
      v-else
      id="weight-validation-untouched"
      :valid="true"
      message=""
    />
  </div>
</template>

<style scoped lang="scss">
.question-weight-input {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.question {
  margin: var(--Spacing-spacing-2) 0;
  text-align: center;
  max-width: 700px;
  @include font-display-3;
}

.input-container {
  display: flex;
  align-items: center;
  gap: var(--Spacing-spacing-2);
}

.input {
  min-width: 90px;
}

.input :deep(input) {
  text-align: center;
}

.unit-label {
  @include font-body;
  color: var(--Neutral-UI-neutral-ui-100);
  min-width: 25px;
}
</style>
